import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { sumLengths } from '@openx/utils/lib/sumLengths.ts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppNameSuggestion, InvalidAppNameWithSuggestions, InvalidItemsError } from '../types.ts';

import { AlertAction } from './components/AlertAction.tsx';
import { AppNameSuggestionsV2 } from './components/AppNameSuggestionsV2.tsx';
import { ErrorDetails } from './components/ErrorDetails.tsx';

interface InvalidItemsProps {
  errorText?: string;
  errors: InvalidItemsError[];
  /** V2: Invalid items with suggestions (optional) */
  invalidItemsWithSuggestions?: InvalidAppNameWithSuggestions[];
  /** V2: Callback when a suggestion is added */
  onAddSuggestion?: (suggestion: AppNameSuggestion, invalidName: string) => void;
}

export function InvalidItems(props: InvalidItemsProps) {
  const { errorText, errors, invalidItemsWithSuggestions, onAddSuggestion } = props;
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);

  const nonValidItems = useMemo(() => sumLengths(errors, error => error.invalidItems.length), [errors]);

  const itemText = nonValidItems > 1 ? t('items were') : t('item was');

  // V2: Check if we have suggestions to display
  const hasV2Suggestions = invalidItemsWithSuggestions && invalidItemsWithSuggestions.length > 0 && onAddSuggestion;

  // V2: Custom error text for suggestions
  const v2ErrorText = hasV2Suggestions
    ? t("We couldn't validate these App Names. Please check the suggested alternatives.")
    : errorText;

  return (
    <Box sx={theme => ({ width: '100%', mr: 1, mb: 1, backgroundColor: theme.palette.error.light })}>
      <Alert
        severity="error"
        action={<AlertAction showErrors={showErrors} setShowErrors={setShowErrors} />}
        data-test="invalid-items-bulk-message"
        sx={{ display: 'flex', alignItems: 'center', '.MuiAlert-action': { paddingTop: '0' } }}
      >
        <Typography variant="body1">
          <b>{t('{num} {itemText} removed', { itemText, num: nonValidItems })}</b>
        </Typography>
        {showErrors && v2ErrorText ? <Typography variant="body1">{v2ErrorText}</Typography> : null}
      </Alert>

      <Box sx={{ pr: 2, pl: 2 }}>
        {showErrors && hasV2Suggestions ? (
          <AppNameSuggestionsV2
            invalidItemsWithSuggestions={invalidItemsWithSuggestions}
            onAddSuggestion={onAddSuggestion}
          />
        ) : (
          showErrors && <ErrorDetails errors={errors} />
        )}
      </Box>
    </Box>
  );
}
